import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./PrePickPage.scss";
import * as itemService from "../../Services/Http/ItemService";
import * as orderService from "../../Services/Http/OrderService";
import { useOrder } from "../../Context/useOrder";
import { Product } from "../../Models/Product";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { PrePickItemRequest } from "../../Models/PrePickItemRequest";
import { PrePickItemResponse } from "../../Models/PrePickItemResponse";
import { UnPrePickItemRequest } from "../../Models/UnPrePickItemRequest";
import { BsTrash } from "react-icons/bs";
import { ScanItemResponse } from "../../Models/ScanItemResponse";
import { Button } from "react-bootstrap";
import { Order } from "../../Models/Order";
import { LockUnlockResponse } from "../../Models/LockUnlockResponse";
import { GenericResponse } from "../../Models/GenericResponse";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import ImgWithPlaceholder from "../../Components/ImgWithPlaceholder";
import ImageCarousel from "../../Components/ImageCarousel";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";
import { LockUnlockRequest } from "../../Models/LockUnlockRequest";
import { useAuth } from "../../Context/useAuth";
import { Modal } from "react-bootstrap";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

type ScanObject = {
  binNo?: string;
  binCode?: string;
  locationNo?: string;
  serialNo?: string;
  stockCode?: string;
  stockNo?: string;
  stockDesc?: string;
  scanFactor?: string;
  status?: number;
  scanResponseStatus?: ScanResponseStatusType;
  transNo?: string;
  orderCompanyNo?: number;
  detailNo?: number;
  prePickQty?: number;
};

type ScanResponseStatusType = "error" | "success" | "scanbin";
const scanBarcodePlaceholderMessage = "Scan pallet or product barcode";
const scanBarcodeOrQryPlaceholderMessage = "Scan barcode or enter quantity";
const scanBinBarcodePlaceholderMessage = "Scan Bin barcode";
//tolerance for floating-point drift when fractional pack factors accumulate over repeated scans
const QTY_EPSILON = 1e-6;

const PrePickPage = observer((props: Props) => {
  const { getCurrentOrder, addToCart } = useOrder();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);
  const [modal3Open, setModal3Open] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [unprepickingDetailNo, setUnprepickingDetailNo] = useState<number | undefined>(undefined);
  const [completeOrderNumber, setCompleteOrderNumber] = useState<string>("");
  const [scanBarcodePlaceholder, setScanBarcodePlaceholder] = useState<string>(scanBarcodePlaceholderMessage);
  const scanObject = useRef<ScanObject | null>(null);
  const locked = useRef<boolean>(false);
  const finishButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);
  const { getSettingNumber } = useAuth();

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  const lock = useCallback(
    async (order: Order | null): Promise<GenericResponse> => {
      return new Promise<GenericResponse>((resolve, reject) => {
        if (order) {
          const model: LockUnlockRequest = {
            transNo: order.transNo || "",
            orderCompanyNo: order.companyNo || 0,
          };
          orderService
            .lockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((lockResponse: LockUnlockResponse) => {
              if (!lockResponse) {
                return resolve({ success: false, errorMessage: "Couldn't acquire order lock." });
              } else if (!lockResponse.success) {
                return resolve({ success: false, errorMessage: "Current order is locked by another user." });
              } else {
                locked.current = true;
                return resolve({ success: true, errorMessage: "" });
              }
            })
            .catch((error: any) => reject(error));
        } else {
          return reject("Order not found");
        }
      });
    },
    [dataStore.dbKey, dataStore.serverUrl],
  );

  const unlock = useCallback(
    async (order: Order | null): Promise<GenericResponse> => {
      return new Promise<GenericResponse>((resolve, reject) => {
        if (order) {
          const model: LockUnlockRequest = {
            transNo: order.transNo || "",
            orderCompanyNo: order.companyNo || 0,
          };
          orderService
            .unlockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((unlockResponse: LockUnlockResponse) => {
              if (!unlockResponse) {
                return resolve({ success: false, errorMessage: "Couldn't acquire unlock." });
              } else if (!unlockResponse.success) {
                return resolve({ success: false, errorMessage: "There was an error unlocking the order." });
              } else {
                locked.current = false;
                return resolve({ success: true, errorMessage: "" });
              }
            })
            .catch((error: any) => reject(error));
        } else {
          return reject("Order not found");
        }
      });
    },
    [dataStore.dbKey, dataStore.serverUrl],
  );

  const isFullyPrePicked = useCallback((order: Order | null = null): boolean => {
    if (!order || !order.cartLines) {
      return false;
    }
    const incompleteLines = order.cartLines.find((product) => (product.qty || 0) < (product.qtyOrdered || 0));
    return !incompleteLines;
  }, []);

  const showIsComplete = (orderNo: string) => {
    setCompleteOrderNumber(orderNo);
    if (orderNo) {
      setTimeout(() => {
        if (finishButtonRef.current) {
          finishButtonRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  };

  const checkIfOrderComplete = useCallback((): void => {
    const order = getCurrentOrder();
    if (!order || !order.cartLines) {
      return;
    }
    if (isFullyPrePicked(order)) {
      showIsComplete(order.transRefCode || "");
    }
  }, [getCurrentOrder, isFullyPrePicked]);

  const parseDecimal = useCallback((n: number): number => {
    let decimalCount = Math.floor(n) === n ? 0 : n.toString().split(".")[1]?.length || 0;
    if (decimalCount === 0) return Math.floor(n);
    if (decimalCount > 3) decimalCount = 3;
    return parseFloat(n.toFixed(decimalCount));
  }, []);

  const onQtySuccessfullyChanged = useCallback(
    (replacedQty: number, row: Product, highlightClass: string = "list-group-item-success"): void => {
      row.qty = parseDecimal(replacedQty);
      if (row.qty < 0) row.qty = 0;
      if (row.qty > (row.qtyOrdered || 0)) row.qty = row.qtyOrdered || 0;
      const products: Product[] = [{ ...row }];
      addToCart(products, true);
      scrollToProduct({ ...row }, highlightClass);
    },
    [addToCart, parseDecimal],
  );

  const scrollToProduct = (product: Product, highlightClass: string = "list-group-item-success") => {
    if (product?.detailNo !== undefined) {
      const element = document.getElementById(`stockno_${product.detailNo}`);
      if (element) {
        if (searchBarRef.current) {
          const stickyTopOffset = parseFloat(window.getComputedStyle(searchBarRef.current).top) || 0;
          element.style.scrollMarginTop = `${stickyTopOffset + searchBarRef.current.offsetHeight + 8}px`;
        }
        element.scrollIntoView({
          behavior: "smooth",
        });
        element.classList.add(highlightClass);
        highlightedElementRef.current = element;
      }
      searchBoxInputRef.current?.focus();
    }
  };

  const handleUnPrePickLine = useCallback(
    (e: React.MouseEvent, product: Product): void => {
      e.stopPropagation();
      const order = getCurrentOrder();
      if (!order || !product.qty) {
        return;
      }
      if (!window.confirm(`Un-prepick ${product.stockDesc} [${product.stockCode}]?`)) {
        return;
      }
      const element = document.getElementById(`stockno_${product.detailNo}`);
      if (element) {
        element.classList.remove("list-group-item-success", "list-group-item-danger");
      }
      if (highlightedElementRef.current === element) {
        highlightedElementRef.current = null;
      }
      const model: UnPrePickItemRequest = {
        transNo: order.transNo,
        detailNo: product.detailNo,
        stockCode: product.stockCode,
        orderCompanyNo: order.companyNo,
        prePickQty: product.qty,
      };
      setUnprepickingDetailNo(product.detailNo);
      itemService
        .unPrePickItemAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((unPrePickItemResponse: PrePickItemResponse) => {
          if (unPrePickItemResponse.error) {
            errorSound.play();
            toast.error(unPrePickItemResponse.error, { autoClose: false });
          } else {
            onQtySuccessfullyChanged(unPrePickItemResponse.prePickQty || 0, product, "list-group-item-danger");
            toast.success(`Un-prepicked ${product.stockDesc}`, { autoClose: 1000 });
            checkIfOrderComplete();
          }
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        })
        .finally(() => {
          setUnprepickingDetailNo(undefined);
        });
    },
    [getCurrentOrder, onQtySuccessfullyChanged, checkIfOrderComplete, dataStore.serverUrl, dataStore.dbKey],
  );

  const isOverPrePicked = useCallback((product: Product | undefined, addedQty: number): boolean => {
    if (!product || addedQty <= 0) return false;
    return (product.qty || 0) + addedQty > (product.qtyOrdered || 0) + QTY_EPSILON;
  }, []);

  const formatQtyExceededMessage = (product: Product, addedQty: number): string => {
    const scannedQty = (product.qty || 0) + addedQty;
    const orderedQty = product.qtyOrdered || 0;
    return `Scanned quantity (${scannedQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}) exceeds ordered quantity (${orderedQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })})`;
  };

  const getBarcodeQty = useCallback(
    (barcode: string): number => {
      if (!barcode || !barcode.trim() || isNaN(Number(barcode.trim()))) return 0;
      let amountOfDigits: number = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAQtyDigits");
      if (amountOfDigits <= 0) amountOfDigits = 2;
      if (barcode.trim().length <= amountOfDigits) return parseInt(barcode.trim(), 10);
      return 0;
    },
    [dataStore.dbKey, dataStore.serverUrl, getSettingNumber],
  );

  const showScanMessage = (scanItemResponse: ScanItemResponse, isSecondScan: boolean = false): ScanResponseStatusType => {
    let message = "";
    let status: ScanResponseStatusType = "error";
    if (scanItemResponse) {
      switch (scanItemResponse.status) {
        case 1:
          status = "success";
          message = "";
          break;
        case 2:
          message = "Barcode not found";
          break;
        case 3:
          if (isSecondScan) {
            message = "Bin not found";
          } else {
            message = "Now scan Bin";
            status = "scanbin";
          }
          break;
        case 4:
          message = "Stock code not found in Bin";
          break;
        case 5:
          message = "Bin not found";
          break;
        case 6:
          message = "Bin was found with wrong stock code";
          break;
        case 0:
        default:
          message = "Error occurred";
          break;
      }
    }
    setSearchTerm("");
    if (status === "success") {
      if (message) toast.success(message, { autoClose: 2000 });
    } else if (status === "error") {
      errorSound.play();
      toast.error(message, { autoClose: 2000 });
    } else if (status === "scanbin") {
      questionSound.play();
      toast.info(message, { autoClose: 2000 });
    }
    if (status === "success") {
      setScanBarcodePlaceholder(scanBarcodeOrQryPlaceholderMessage);
    } else {
      setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
    }
    return status;
  };

  const calculateAddedQty = (scanFactor: string, product: Product): number => {
    switch (scanFactor.toLowerCase()) {
      case "unit": return product.scanUnitFactor || 1;
      case "ctn": return product.scanCtnFactor || 1;
      case "inn": return product.scanInnerFactor || 1;
      case "serial": return product.scanSerialFactor || 1;
      case "bincode":
      default: return 1;
    }
  };

  const isBarcodeBinCode = (barcode: string): boolean => {
    const binRegex = new RegExp(/[A-Za-z][A-Za-z12]-[A-Za-z]-\d-\d\d/);
    return binRegex.test(barcode);
  };

  const doPrePickRequest = useCallback(
    (order: Order, foundCartLine: Product, addedQty: number): void => {
      const prePickItemRequest: PrePickItemRequest = {
        transNo: order.transNo,
        detailNo: foundCartLine.detailNo,
        stockCode: foundCartLine.stockCode,
        prePickQty: addedQty,
        orderCompanyNo: order.companyNo,
      };
      itemService
        .prePickItemAPI(prePickItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((prePickItemResponse: PrePickItemResponse) => {
          if (prePickItemResponse.error) {
            setSearchTerm("");
            errorSound.play();
            toast.error(prePickItemResponse.error, { autoClose: false });
          } else {
            setSearchTerm("");
            onQtySuccessfullyChanged(prePickItemResponse.prePickQty || 0, foundCartLine);
            if (prePickItemResponse.prePickQty !== undefined) {
              successSound.play();
              toast.success(
                `Added ${addedQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}`,
                { autoClose: 200 },
              );
            } else {
              toast.warn(`Not added (server error)`, { autoClose: 200 });
            }
            checkIfOrderComplete();
          }
        })
        .catch((error: any) => {
          setSearchTerm("");
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [dataStore.serverUrl, dataStore.dbKey, onQtySuccessfullyChanged, checkIfOrderComplete],
  );

  const doFirstScan = useCallback(
    (barcode: string): void => {
      scanObject.current = null;
      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 200 });
        return;
      }
      itemService
        .scanItemFirstAPI(barcode, dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo, order.transNo)
        .then((scanItemResponse: ScanItemResponse) => {
          const scanresp: ScanResponseStatusType = showScanMessage(scanItemResponse);
          scanObject.current = {
            ...scanItemResponse,
            scanResponseStatus: scanresp,
            orderCompanyNo: order.companyNo,
            transNo: order.transNo,
          };
          if (scanresp === "error") return;

          const foundCartLine =
            order.cartLines!.find(
              (x) => x.stockCode === scanItemResponse.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
            ) || order.cartLines!.find((x) => x.stockCode === scanItemResponse.stockCode);
          if (!foundCartLine) {
            setSearchTerm("");
            errorSound.play();
            toast.error("Current order does not contain this product", { autoClose: 1000 });
            return;
          }
          if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
            setSearchTerm("");
            errorSound.play();
            toast.error("All products with the current barcode have already been scanned", { autoClose: 2000 });
            return;
          }
          const addedQty: number = calculateAddedQty(scanItemResponse.scanFactor!, foundCartLine);
          if (isOverPrePicked(foundCartLine, addedQty)) {
            setSearchTerm("");
            toast.error(formatQtyExceededMessage(foundCartLine, addedQty), { autoClose: 2000 });
            return;
          }
          scanObject.current = {
            ...scanObject.current,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            prePickQty: addedQty,
          };
          if (scanresp === "scanbin") {
            setScanBarcodePlaceholder(scanBinBarcodePlaceholderMessage);
            return;
          } else {
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
          }
          doPrePickRequest(order, foundCartLine, addedQty);
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [getCurrentOrder, isOverPrePicked, doPrePickRequest, dataStore.serverUrl, dataStore.dbKey],
  );

  const doSecondScan = useCallback(
    (binCode: string): void => {
      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 200 });
        return;
      }
      const foundCartLine =
        order.cartLines!.find(
          (x) => x.stockCode === scanObject.current?.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
        ) || order.cartLines!.find((x) => x.stockCode === scanObject.current?.stockCode);
      if (!foundCartLine) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Current order does not contain this product", { autoClose: 1000 });
        return;
      }
      if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
        setSearchTerm("");
        errorSound.play();
        toast.error("All products with the current barcode have already been scanned", { autoClose: 1000 });
        return;
      }
      itemService
        .scanItemSecondAPI(binCode, foundCartLine.stockCode!, dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo, order.transNo)
        .then((scanItemResponse: ScanItemResponse) => {
          const scanresp: ScanResponseStatusType = showScanMessage(scanItemResponse, true);
          scanObject.current = { ...scanObject.current, ...scanItemResponse, scanResponseStatus: scanresp };
          if (scanresp === "error") return;

          const addedQty: number = calculateAddedQty(scanItemResponse.scanFactor!, foundCartLine);
          if (isOverPrePicked(foundCartLine, addedQty)) {
            setSearchTerm("");
            toast.error(formatQtyExceededMessage(foundCartLine, addedQty), { autoClose: 2000 });
            return;
          }
          scanObject.current = {
            ...scanObject.current,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            prePickQty: addedQty,
          };
          if (scanresp === "scanbin") {
            setScanBarcodePlaceholder(scanBinBarcodePlaceholderMessage);
            return;
          } else {
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
          }
          doPrePickRequest(order, foundCartLine, addedQty);
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [getCurrentOrder, isOverPrePicked, doPrePickRequest, dataStore.serverUrl, dataStore.dbKey],
  );

  const doQtyScan = useCallback(
    (enteredQty: number): void => {
      if (enteredQty <= 0) return;
      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 1000 });
        return;
      }
      const foundCartLine =
        order.cartLines!.find(
          (x) => x.stockCode === scanObject.current?.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
        ) || order.cartLines!.find((x) => x.stockCode === scanObject.current?.stockCode);
      if (!foundCartLine) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Current order does not contain this product", { autoClose: 2000 });
        return;
      }
      if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
        setSearchTerm("");
        errorSound.play();
        toast.error("All products with the current barcode have already been scanned", { autoClose: 2000 });
        return;
      }
      const newQty = enteredQty - (scanObject.current?.prePickQty || 0);
      if (newQty <= 0) {
        toast.error("New quantity must be higher than previous quantity", { autoClose: 2000 });
        return;
      }
      if (isOverPrePicked(foundCartLine, newQty)) {
        setSearchTerm("");
        toast.error(formatQtyExceededMessage(foundCartLine, newQty), { autoClose: 2000 });
        return;
      }
      if (!scanObject.current) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not current", { autoClose: 2000 });
        return;
      }
      const prePickItemRequest: PrePickItemRequest = {
        transNo: scanObject.current.transNo,
        detailNo: scanObject.current.detailNo,
        stockCode: scanObject.current.stockCode,
        prePickQty: newQty,
        orderCompanyNo: scanObject.current.orderCompanyNo,
      };
      itemService
        .prePickItemAPI(prePickItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((prePickItemResponse: PrePickItemResponse) => {
          scanObject.current = null;
          if (prePickItemResponse.error) {
            setSearchTerm("");
            errorSound.play();
            toast.error(prePickItemResponse.error, { autoClose: false });
          } else {
            setSearchTerm("");
            onQtySuccessfullyChanged(prePickItemResponse.prePickQty || 0, foundCartLine);
            successSound.play();
            toast.success(`Quantity updated`, { autoClose: 200 });
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
            checkIfOrderComplete();
          }
        })
        .catch((error: any) => {
          scanObject.current = null;
          setSearchTerm("");
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [checkIfOrderComplete, getCurrentOrder, onQtySuccessfullyChanged, isOverPrePicked, dataStore.serverUrl, dataStore.dbKey],
  );

  const onBarcodeScanned = useCallback(
    (barcode: string): void => {
      if (highlightedElementRef.current) {
        highlightedElementRef.current.classList.remove("list-group-item-success", "list-group-item-danger");
        highlightedElementRef.current = null;
      }
      const order = getCurrentOrder();
      if (order && isFullyPrePicked(order)) {
        toast.info("This order is fully pre-picked.");
        return;
      }
      if (scanObject.current?.scanResponseStatus === "scanbin" && isBarcodeBinCode(barcode)) {
        doSecondScan(barcode);
        return;
      }
      if (scanObject.current?.scanResponseStatus === "success") {
        const enteredQty = getBarcodeQty(barcode);
        if (enteredQty > 0) {
          doQtyScan(enteredQty);
          return;
        }
      }
      doFirstScan(barcode);
    },
    [scanObject, doFirstScan, doSecondScan, doQtyScan, getCurrentOrder, getBarcodeQty, isFullyPrePicked],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      if (event.target && event.target instanceof HTMLInputElement && event.target.name === searchBoxInputRef.current!.name) return;
      const keyPressed = event.key ? event.key : "";
      if (keyPressed === "") return;
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          onBarcodeScanned(oldState);
          return oldState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => oldState + keyPressed);
      }
    },
    [onBarcodeScanned],
  );

  const bindGrid = useCallback(
    (order: Order | null = null) => {
      if (order) {
        lock(order)
          .then((resp: GenericResponse) => {
            if (resp.success) {
              if (isFullyPrePicked(order)) {
                toast.info("This order is fully pre-picked.");
              } else {
                checkIfOrderComplete();
              }
            } else {
              toast.error(resp.errorMessage, { autoClose: false });
              return navigate("/prepick");
            }
          })
          .catch((error: any) => {
            toast.error(error.message || error, { autoClose: false });
          });
      }
    },
    [lock, navigate, checkIfOrderComplete, isFullyPrePicked],
  );

  useEffect(() => {
    const order = getCurrentOrder();
    bindGrid(order);
    return () => {
      if (!!locked.current) {
        unlock(order)
          .then((resp: GenericResponse) => {
            if (!resp.success) toast.error(resp.errorMessage, { autoClose: false });
          })
          .catch((error: any) => {
            toast.error(error.message || error, { autoClose: false });
          });
      }
    };
  }, [bindGrid, getCurrentOrder, unlock]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => window.removeEventListener("keydown", onKeyPressed);
  }, [onKeyPressed]);

  const handleSearchChange = (event: any) => setSearchTerm(event.target.value);

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onBarcodeScanned(searchTerm);
    }
  };

  const onSearchClicked = () => onBarcodeScanned(searchTerm);

  const getPrePickedCssClassName = (product: Product): string => {
    let css = "qty-badge badge";
    css += product.qtyOrdered === product.qty ? " bg-success" : " bg-secondary";
    return css;
  };

  const onFinish = () => navigate("/prepick");

  const onCloseModal3 = () => {
    setModal3Open(false);
    setSelectedProduct(undefined);
  };

  return (
    <>
      <OrderStatusBar allowPrint={true} />
      <div className="container-limited-width">
        <div className="row sticky-top-3 search-bar" ref={searchBarRef}>
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder={scanBarcodePlaceholder}
                autoCapitalize="none"
                autoComplete="off"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 mt-2 p-0">
            <ul className="list-group list-group-flush p-0">
              {getCurrentOrder() &&
                getCurrentOrder()!.cartLines!.map((product, index) => {
                  return (
                    <li
                      id={`stockno_${product.detailNo}`}
                      key={`${product.stockNo}_${index}`}
                      className="list-group-item d-flex align-items-start p-1 product-list-item"
                      onClick={() => {
                        if (product.images) {
                          setSelectedProduct(product);
                          setModal3Open(true);
                        }
                      }}
                    >
                      <div className="d-flex flex-column product-image-container">
                        <div className="d-flex">
                          {product.images && product.images.length > 0 ? (
                            <ImgWithPlaceholder
                              src={product.images[0].thumbImageUrl}
                              alt={product.stockDesc}
                              className="product-image"
                              fallback={<img src="/assets/images/no-image-100x100.png" alt="" className="rounded product-image" />}
                            />
                          ) : (
                            <img src="/assets/images/no-image-100x100.png" alt="" className="rounded product-image" />
                          )}
                        </div>
                        {product.showOutstanding && (
                          <div className="d-flex align-self-center align-items-center my-1">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row">
                                <span className={getPrePickedCssClassName(product)}>
                                  {product.qty!.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
                                  &nbsp;/&nbsp;
                                  {product.qtyOrdered!.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
                                </span>
                                <div className="d-flex pickunit align-items-center">{product.pickUnit}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column flex-grow-1 mx-2">
                        <div className="fw-bold">{product.stockDesc}</div>
                        <div className="d-flex justify-content-between">
                          <div className="text-muted">{product.stockCode}</div>
                        </div>
                        <div className="d-flex flex-column">
                          <div className="text-muted">Barcode: {product.stockBarcode}</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-start">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={!product.qty || unprepickingDetailNo === product.detailNo}
                          onClick={(e) => handleUnPrePickLine(e, product)}
                          title="Un-prepick this line"
                        >
                          <BsTrash />
                        </Button>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
        {completeOrderNumber && (
          <div className="row">
            <div className="col d-flex">
              <Button variant="success" className="d-flex flex-fill justify-content-center" onClick={onFinish} ref={finishButtonRef}>
                Pre-Pick {completeOrderNumber} is complete
                <br />
                Click to continue
              </Button>
            </div>
          </div>
        )}
      </div>
      <Modal show={modal3Open} onHide={onCloseModal3} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedProduct?.stockDesc}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProduct?.images && <ImageCarousel imageUrls={selectedProduct.images?.filter((i) => i !== undefined && i.largeImageUrl !== undefined)?.map((i) => i.largeImageUrl!) || []} alt={selectedProduct.stockDesc} />}
        </Modal.Body>
      </Modal>
    </>
  );
});

export default PrePickPage;
